/**
 * Lógica pura de «Mi trabajo» (REQ-045 flujo de jornada, 045b tarea ya realizada, 046e bloques).
 * Sin React ni red: formateadores, reglas de pestaña por defecto y etiquetas. Con tests en __tests__/flowLogic.test.ts.
 */
import { fmtHm, type BlockConflict, type CloseAction, type FlowPrevious, type PreviousOrigin, type SkippedDay, type TeamRow, type WorkdayFlow, type WorkdayWrap } from '../../stores/activityApi';

export type FlowTab = 'previous' | 'current' | 'next';

export const FLOW_TABS: { id: FlowTab; label: string; icon: string }[] = [
  { id: 'previous', label: 'ANTERIOR', icon: 'arrow_back' },
  { id: 'current', label: 'EN EJECUCIÓN', icon: 'fiber_manual_record' },
  { id: 'next', label: 'SIGUIENTE', icon: 'arrow_forward' },
];

/** Pestaña inicial: la elegida por la persona manda; si no, en ejecución → siguiente → anterior. */
export function defaultFlowTab(flow: Pick<WorkdayFlow, 'previous' | 'current' | 'next'> | null, explicit: FlowTab | null): FlowTab {
  if (explicit) return explicit;
  if (!flow) return 'current';
  if (flow.current) return 'current';
  if (flow.next) return 'next';
  if (flow.previous) return 'previous';
  return 'current';
}

/** HH:MM en es-ES. */
export const fmtClockTime = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '';

/** «6/10, 13:20». */
export const fmtDayTime = (iso: string): string =>
  new Date(iso).toLocaleString('es-ES', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });

/** «05 oct». */
export const fmtShortDate = (d: string): string =>
  new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });

export const ORIGIN_META: Record<PreviousOrigin, { label: string; cls: string }> = {
  EXECUTED_IN_BARMETHUB: { label: 'Ejecutada en BarmetHub', cls: 'border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]' },
  MARKED_DONE: { label: 'Marcada como hecha (sin trabajo registrado)', cls: 'border-outline text-on-surface-variant' },
  ALREADY_DONE: { label: 'Ya realizada fuera de BarmetHub (declarada)', cls: 'border-[#60a5fa]/40 bg-[#60a5fa]/10 text-[#60a5fa]' },
};

/** Etiqueta y valor de la fecha de la tarea anterior; para «ya realizada» solo hay día si no se indicó hora. */
export function previousWhen(p: Pick<FlowPrevious, 'origin' | 'finishedAt' | 'finishedPrecision' | 'finishedDate'>): { label: string; value: string } {
  if (p.origin !== 'ALREADY_DONE') return { label: 'Finalizó', value: fmtDayTime(p.finishedAt) };
  if (p.finishedPrecision === 'DATE' && p.finishedDate) return { label: 'Se realizó', value: `${fmtShortDate(p.finishedDate)} (hora no indicada)` };
  return { label: 'Se realizó', value: fmtDayTime(p.finishedAt) };
}

/** «Resultado» siempre se muestra; sin dato, se dice. */
export const previousResult = (r: string | null | undefined): string => (r && r.trim() ? r : 'Sin resultado registrado');

export const previousAttributed = (sec: number | null): string => (sec === null ? 'No hay tiempo registrado para esta tarea' : fmtHm(sec));

/** Estado de Nexus sobre la tarea en ejecución. */
export function nexusPill(nexus: { status: string }[]): { text: string; cls: string } | null {
  if (nexus.some((n) => n.status === 'WAITING_USER')) return { text: 'Espera tu respuesta', cls: 'border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]' };
  if (nexus.some((n) => n.status === 'RUNNING')) return { text: 'Trabajando', cls: 'border-[#3b82f6]/40 bg-[#3b82f6]/10 text-[#60a5fa]' };
  if (nexus.some((n) => n.status === 'QUEUED')) return { text: 'En cola', cls: 'border-[#3b82f6]/40 bg-[#3b82f6]/10 text-[#60a5fa]' };
  return null;
}

/** Segundos transcurridos desde la respuesta del servidor, sin retroceder. */
export const tickedElapsed = (elapsedSec: number, fetchedAtMs: number, nowMs: number): number =>
  elapsedSec + Math.max(0, Math.floor((nowMs - fetchedAtMs) / 1000));

/** Subtítulo de la franja de Nexus: si Nexus trabaja sobre la tarea en ejecución, lo dice. */
export const nexusStripNote = (nexus: { status: string }[] | undefined): string => nexusPill(nexus ?? [])?.text ?? 'tu asistente para resolver tareas';

export const unavailableLine = (list: { label: string }[]): string | null =>
  list.length ? `No disponible aún en BarmetHub: ${list.map((u) => u.label.toLowerCase()).join(', ')}. No se muestran datos inventados.` : null;

/* ── Finalizar jornada ── */
export const CLOSE_ACTIONS_UI: { action: CloseAction; title: string; desc: string }[] = [
  { action: 'FINISH_TASK', title: 'Finalizar tarea', desc: 'Se marca como hecha y se cierran sus ejecuciones de Nexus terminadas.' },
  { action: 'LEAVE_PENDING', title: 'Dejar pendiente', desc: 'Vuelve a «Por hacer» si estaba en curso. Se registra el tiempo ya trabajado.' },
  { action: 'MARK_BLOCKED', title: 'Marcar bloqueada', desc: 'Queda bloqueada hasta que alguien la desbloquee.' },
  { action: 'CONTINUE_NEXT_DAY', title: 'Continuar en la próxima jornada', desc: 'Se conserva en curso; mañana aparecerá como lo siguiente que hacer.' },
];

/** Texto de ayuda de cada opción de cierre (el título lo da la API en `options`). */
export const closeActionDesc = (a: string): string => CLOSE_ACTIONS_UI.find((o) => o.action === a)?.desc ?? '';

export const closeDecisionText = (a: CloseAction | null | undefined): string | null =>
  ({ FINISH_TASK: 'Tarea finalizada', LEAVE_PENDING: 'Dejada pendiente', CONTINUE_NEXT_DAY: 'Continúa en la próxima jornada', MARK_BLOCKED: 'Marcada bloqueada' } as Record<string, string>)[a ?? ''] ?? null;

export function activeLegend(tasks: { title: string }[]): string {
  const names = tasks.length ? tasks.map((t) => t.title).join(' · ') : 'sin tarea concreta';
  return `Tienes trabajo activo: ${names}. ¿Qué hacemos con la tarea actual?`;
}

export function exceptionsLine(ex: WorkdayWrap['exceptions']): string | null {
  if (!ex || (ex.pendingSec <= 0 && ex.meetingsToConfirm <= 0 && ex.callsWithoutProject <= 0)) return null;
  return `Quedan excepciones: ${fmtHm(ex.pendingSec)} sin clasificar, ${ex.meetingsToConfirm} reunión(es) por confirmar, ${ex.callsWithoutProject} llamada(s) sin proyecto.`;
}

/** Con «Finalizar tarea» y Nexus trabajando, la opción no está disponible. */
export const isCloseActionDisabled = (a: CloseAction, nexusRunning: number): boolean => a === 'FINISH_TASK' && nexusRunning > 0;

/** El botón de confirmar exige elegir cuando hay trabajo activo. */
export const canConfirmClose = (needsDecision: boolean, chosen: CloseAction | null): boolean => !needsDecision || !!chosen;

/** Hora de salida de la respuesta de cierre (último CLOCK_OUT del timeline), o null. */
export function exitTimeOf(timeline: { type: string; at: string }[] | undefined): string | null {
  const out = (timeline ?? []).filter((t) => t.type === 'CLOCK_OUT').map((t) => t.at).sort();
  return out.length ? fmtClockTime(out[out.length - 1]) : null;
}

/* ── Tarea ya realizada ── */
/** Duración de «Tarea ya realizada»: vacía = no se informa; si se informa, entero de 1 a 1440 minutos. */
export function parseDoneDuration(raw: string): { state: 'empty' } | { state: 'ok'; minutes: number } | { state: 'invalid' } {
  const t = raw.trim();
  if (!t) return { state: 'empty' };
  const n = Number(t);
  return Number.isInteger(n) && n >= 1 && n <= 1440 ? { state: 'ok', minutes: n } : { state: 'invalid' };
}
export const DONE_DURATION_ERROR = 'La duración debe ser un número de minutos entre 1 y 1440.';

/** Línea de ayuda: en cuanto se informa una duración, se avisa de que se imputará (borrador). */
export const alreadyDoneHint = (duration: string): string =>
  duration.trim()
    ? 'Con fecha y duración se imputará ese tiempo al proyecto y a la tarea (borrador), sin sumarlo a la jornada.'
    : 'Sin duración no se imputa tiempo: BarmetHub no la inventa.';

/** Aviso tras guardar: la nota de tiempo de la API solo si se imputó tiempo; si no, se dice que la jornada no cambia. */
export const alreadyDoneToast = (effects?: { timeAttributed?: boolean; timeNote?: string } | null): string =>
  `Tarea marcada como ya realizada. ${effects?.timeAttributed && effects.timeNote ? effects.timeNote : 'No se ha sumado nada a tu jornada de hoy.'}`;

export const addDays = (date: string, n: number): string => {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** La fecha es obligatoria y no puede ser futura (comparación de YYYY-MM-DD). */
export const isValidDoneDate = (date: string, today: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(date) && date <= today;

export function buildAlreadyDoneBody(f: { date: string; time: string; duration: string; note: string; evidence: string }, source: 'MY_TASKS' | 'NEXUS_RECOMMENDATION') {
  const body: { doneDate: string; source: typeof source; doneTime?: string; durationMin?: number; note?: string; evidence?: string } = { doneDate: f.date, source };
  if (f.time) body.doneTime = f.time;
  const dur = parseDoneDuration(f.duration);
  if (dur.state === 'ok') body.durationMin = dur.minutes;
  if (f.note.trim()) body.note = f.note.trim();
  if (f.evidence.trim()) body.evidence = f.evidence.trim();
  return body;
}

/* ── Equipo en ejecución (Dirección) ── */
export const TEAM_LABEL_CLS: Record<string, string> = {
  'EN JORNADA': 'border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]',
  'JORNADA FINALIZADA': 'border-outline text-on-surface-variant',
  'SIN FICHAR': 'border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]',
};
export const teamPillCls = (label: string): string => TEAM_LABEL_CLS[label] ?? '';
export const teamStateText = (r: Pick<TeamRow, 'label' | 'workday'>): string =>
  r.workday.state === 'FINISHED' && r.workday.clockOut ? `${r.label} · ${fmtClockTime(r.workday.clockOut)}` : r.label;

/* ── Bloques de trabajo ── */
/** «martes, 6 de octubre». */
export const fmtLongDay = (isoOrDate: string): string =>
  (isoOrDate.length > 10 ? new Date(isoOrDate) : new Date(`${isoOrDate}T12:00:00`)).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });

export const blockRange = (startsAt: string, endsAt: string): string => `${fmtClockTime(startsAt)}–${fmtClockTime(endsAt)}`;

export const QUICK_DURATIONS: { min: number; label: string }[] = [
  { min: 30, label: '30 min' }, { min: 60, label: '60 min' }, { min: 90, label: '90 min' }, { min: 120, label: '120 min (por defecto)' }, { min: 180, label: '180 min' }, { min: 240, label: '240 min' },
];
export const DEFAULT_BLOCK_MIN = 120;
export const DEFAULT_DAY_START = '09:00';
export const DEFAULT_DAY_END = '18:00';

export const BLOCK_STATUS_UI: Record<string, { label: string; cls: string }> = {
  PLANNED: { label: 'Reservado', cls: 'border-[#60a5fa]/40 bg-[#3b82f6]/10 text-[#60a5fa]' },
  IN_PROGRESS: { label: 'En curso', cls: 'border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]' },
  DONE: { label: 'Finalizado', cls: 'border-outline text-outline' },
};

/** La duración de un bloque va de 15 a 480 min. */
export const isValidBlockDuration = (min: number): boolean => Number.isInteger(min) && min >= 15 && min <= 480;

export const SKIP_REASON_TEXT: Record<string, string> = { WORKDAY_FINISHED: 'jornada ya finalizada', NO_SLOT: 'sin hueco suficiente', WEEKEND: 'fin de semana' };
export const skippedDaysLine = (days: SkippedDay[]): string | null =>
  days.length ? `Días saltados: ${days.map((d) => `${fmtLongDay(d.date)} (${SKIP_REASON_TEXT[d.reason] ?? d.reason})`).join(' · ')}` : null;

export const BUSY_TYPE_TEXT: Record<string, string> = { MEETING: 'Reunión', TASK_FIXED: 'Tarea con hora exacta', BLOCK: 'Otro bloque' };
export const busyLine = (c: BlockConflict): string => `${BUSY_TYPE_TEXT[c.type] ?? c.type}: ${c.title} (${blockRange(c.startsAt, c.endsAt)})`;

export const EXCLUDE_REASON_TEXT: Record<string, string> = { BLOCKED: 'bloqueada', CLOSED: 'cerrada', FIXED_TIME: 'ya tiene hora exacta' };
export const excludedLine = (ex: { title: string; reason: string }[]): string | null =>
  ex.length ? `No se proponen: ${ex.map((e) => `${e.title} (${EXCLUDE_REASON_TEXT[e.reason] ?? e.reason})`).join(' · ')}` : null;

export const allocText = (t: { allocatedMinutes: number | null; partial: boolean }): string =>
  t.allocatedMinutes === null ? 'sin estimación' : `${t.allocatedMinutes} min${t.partial ? ' (parcial)' : ''}`;

export const workedLine = (w: { sec: number; coverage: number }): string =>
  `Trabajado en esta franja: ${fmtHm(w.sec)} (${w.coverage} %) · medido por el motor de actividad, no por el bloque`;

/** Intervalo de lectura de bloques: hoy y los 7 días siguientes (fechas de Madrid). */
export const blocksWindow = (today: string): { from: string; to: string } => ({ from: today, to: addDays(today, 7) });
